import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate as RouterNavigate,
} from "react-router-dom";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import Orders from "./pages/Orders";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUserList from "./pages/AdminUserList";
import AdminCreateUser from "./pages/AdminCreateUser";
import AdminEditUser from "./pages/AdminEditUser";
import AdminOrders from "./pages/AdminOrders";
import AdminImageEditor from "./pages/AdminImageEditor";
import AdminGallery from "./pages/AdminGallery";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import LegalNotice from "./pages/LegalNotice";
import AuthCallback from "./pages/AuthCallback";
import EmailVerification from "./pages/EmailVerification";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentResult from "./pages/PaymentResult";
import ProductDetail from "./pages/ProductDetail";
import CategoryPage from "./pages/CategoryPage";
import PaymentExpirationChecker from "./components/PaymentExpirationChecker";

import ProtectedRoute from "./components/ProtectedRoute";
import NavBar from "./components/Navbar";
import Footer from "./components/Footer";
import Breadcrumb from "./components/Breadcrumb";
import { CartProvider } from "./contexts/CartContext";

// ScrollToTop pour remettre la page en haut lors des changements de route
const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Composant wrapper pour afficher conditionnellement la NavBar, Breadcrumb et le Footer
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isAuthRoute =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/email-verification";

  return (
    <div className="d-flex flex-column min-vh-100">
      {!isAdminRoute && !isAuthRoute && <NavBar />}
      {!isAdminRoute && !isAuthRoute && <Breadcrumb />}
      <div className="flex-grow-1">{children}</div>
      {!isAdminRoute && !isAuthRoute && <Footer />}
    </div>
  );
};

function App() {
  return (
    <CartProvider>
      <Router>
        <ScrollToTop />
        <PaymentExpirationChecker />
        <Routes>
          {/* Route publique accessible à tous */}
          <Route
            path="/"
            element={
              <AppLayout>
                <Home />
              </AppLayout>
            }
          />

          {/* Routes d'authentification */}
          <Route
            path="/login"
            element={
              <AppLayout>
                <Login />
              </AppLayout>
            }
          />
          <Route
            path="/register"
            element={
              <AppLayout>
                <Register />
              </AppLayout>
            }
          />

          {/* Route pour la vérification d'email */}
          <Route
            path="/email-verification"
            element={
              <AppLayout>
                <EmailVerification />
              </AppLayout>
            }
          />

          {/* Route pour le callback OAuth */}
          <Route
            path="/auth/callback"
            element={
              <AppLayout>
                <AuthCallback />
              </AppLayout>
            }
          />

          {/* Routes pour les pages légales */}
          <Route
            path="/privacy-policy"
            element={
              <AppLayout>
                <PrivacyPolicy />
              </AppLayout>
            }
          />
          <Route
            path="/terms-of-service"
            element={
              <AppLayout>
                <TermsOfService />
              </AppLayout>
            }
          />
          <Route
            path="/legal-notice"
            element={
              <AppLayout>
                <LegalNotice />
              </AppLayout>
            }
          />

          {/* Routes utilisateur protégées (avec navbar) */}
          <Route
            path="/profile"
            element={
              <AppLayout>
                <ProtectedRoute adminRequired={false}>
                  <Profile />
                </ProtectedRoute>
              </AppLayout>
            }
          />

          {/* Route pour les commandes utilisateur */}
          <Route
            path="/orders"
            element={
              <AppLayout>
                <ProtectedRoute adminRequired={false}>
                  <Orders />
                </ProtectedRoute>
              </AppLayout>
            }
          />

          {/* Routes e-commerce */}
          <Route
            path="/cart"
            element={
              <AppLayout>
                <Cart />
              </AppLayout>
            }
          />
          <Route
            path="/checkout"
            element={
              <AppLayout>
                <Checkout />
              </AppLayout>
            }
          />
          <Route
            path="/payment-success"
            element={
              <AppLayout>
                <PaymentSuccess />
              </AppLayout>
            }
          />
          <Route
            path="/payment-result"
            element={
              <AppLayout>
                <PaymentResult />
              </AppLayout>
            }
          />

          {/* Route pour la page produit */}
          <Route
            path="/product/:id"
            element={
              <AppLayout>
                <ProductDetail />
              </AppLayout>
            }
          />

          {/* Route de réinitialisation du mot de passe */}
          <Route
            path="/reset-password/:token"
            element={
              <AppLayout>
                <ResetPassword />
              </AppLayout>
            }
          />

          {/* Route pour l'éditeur d'image (page plein écran) */}
          <Route
            path="/admin/image-editor"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminImageEditor />
              </ProtectedRoute>
            }
          />

          {/* Route pour la galerie d'images */}
          <Route
            path="/admin/gallery"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminGallery />
              </ProtectedRoute>
            }
          />

          {/* Routes protégées pour les administrateurs */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          >
            {/* Route par défaut de l'admin - Dashboard */}
            <Route
              index
              element={
                <div className="row">
                  <div className="col-12">
                    <h2>Tableau de bord</h2>
                    <p>
                      Bienvenue dans l'interface d'administration de BoxPlaisir.
                    </p>

                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-body">
                            <h5 className="card-title">
                              <i className="bi bi-people me-2"></i>
                              Gestion des utilisateurs
                            </h5>
                            <p className="card-text">
                              Gérer les comptes utilisateurs, créer et modifier
                              les profils.
                            </p>
                            <a href="/admin/users" className="btn btn-primary">
                              Voir les utilisateurs
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-body">
                            <h5 className="card-title">
                              <i className="bi bi-box-seam me-2"></i>
                              Gestion des commandes
                            </h5>
                            <p className="card-text">
                              Suivre et gérer toutes les commandes, mettre à
                              jour les statuts.
                            </p>
                            <a href="/admin/orders" className="btn btn-primary">
                              Voir les commandes
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-body">
                            <h5 className="card-title">
                              <i className="bi bi-image me-2"></i>
                              Éditeur d'image
                            </h5>
                            <p className="card-text">
                              Éditer et préparer les images pour les produits de
                              la boutique.
                            </p>
                            <a
                              href="/admin/image-editor"
                              className="btn btn-primary"
                            >
                              Ouvrir l'éditeur
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-body">
                            <h5 className="card-title">
                              <i className="bi bi-images me-2"></i>
                              Galerie d'images
                            </h5>
                            <p className="card-text">
                              Gérer la galerie d'images, organiser vos favoris.
                            </p>
                            <a
                              href="/admin/gallery"
                              className="btn btn-primary"
                            >
                              Voir la galerie
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-body">
                            <h5 className="card-title">
                              <i className="bi bi-graph-up me-2"></i>
                              Statistiques
                            </h5>
                            <p className="card-text">
                              Voir les performances et les analyses de la
                              boutique.
                            </p>
                            <button className="btn btn-secondary" disabled>
                              Bientôt disponible
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              }
            />

            {/* Gestion des utilisateurs */}
            <Route
              path="users"
              element={
                <ProtectedRoute adminRequired={true}>
                  <AdminUserList />
                </ProtectedRoute>
              }
            />
            <Route
              path="create"
              element={
                <ProtectedRoute adminRequired={true}>
                  <AdminCreateUser />
                </ProtectedRoute>
              }
            />
            <Route
              path="edit/:id"
              element={
                <ProtectedRoute adminRequired={true}>
                  <AdminEditUser />
                </ProtectedRoute>
              }
            />

            {/* Gestion des commandes */}
            <Route
              path="orders"
              element={
                <ProtectedRoute adminRequired={true}>
                  <AdminOrders />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Routes supplémentaires du Navbar */}
          <Route
            path="/category"
            element={
              <AppLayout>
                <CategoryPage />
              </AppLayout>
            }
          />

          <Route
            path="/how-it-works"
            element={
              <AppLayout>
                <div className="container py-5">
                  <h1>Comment ça marche</h1>
                  <p>Cette page est en construction...</p>
                </div>
              </AppLayout>
            }
          />

          <Route
            path="/about"
            element={
              <AppLayout>
                <div className="container py-5">
                  <h1>À propos</h1>
                  <p>Cette page est en construction...</p>
                </div>
              </AppLayout>
            }
          />

          <Route
            path="/contact"
            element={
              <AppLayout>
                <div className="container py-5">
                  <h1>Contact</h1>
                  <p>Cette page est en construction...</p>
                </div>
              </AppLayout>
            }
          />

          <Route
            path="/favorites"
            element={
              <AppLayout>
                <ProtectedRoute adminRequired={false}>
                  <div className="container py-5">
                    <h1>Mes favoris</h1>
                    <p>Cette page est en construction...</p>
                  </div>
                </ProtectedRoute>
              </AppLayout>
            }
          />

          {/* Route de fallback */}
          <Route path="*" element={<RouterNavigate to="/" replace />} />
        </Routes>
      </Router>
    </CartProvider>
  );
}

export default App;